import { INotificationItem, INotificationRolePrefs } from 'types/notificationCenter.interface'
import {
  buildDemoWidgetSummary,
  coverageSites,
  syncCoverageRegistries,
} from 'pages/Shifts/Coverage/coverageDemoStore'
import {
  MANDATORY_NOTIFICATION_CATEGORIES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_TYPE_DEFS,
  notificationTypeDef,
} from './notificationRegistry'

export const USE_NOTIFICATION_MOCKS = true

const readState: { [id: string]: { read: boolean; acknowledged: boolean } } = {}

const rolePrefs: { [roleId: number]: INotificationRolePrefs } = {}

let activeRoleId: number | null = null

export const getRoleNotificationPrefs = (roleId: number): INotificationRolePrefs => {
  const stored = rolePrefs[roleId] || { categories: {}, types: {} }
  const prefs: INotificationRolePrefs = { categories: {}, types: {} }
  NOTIFICATION_CATEGORIES.forEach((c) => {
    prefs.categories[c] = MANDATORY_NOTIFICATION_CATEGORIES.includes(c)
      ? true
      : stored.categories[c] === true
  })
  Object.keys(NOTIFICATION_TYPE_DEFS).forEach((t) => {
    prefs.types[t] = NOTIFICATION_TYPE_DEFS[t].alwaysOn ? true : stored.types[t] !== false
  })
  return prefs
}

export const saveRoleNotificationPrefs = (roleId: number, prefs: INotificationRolePrefs): void => {
  const next: INotificationRolePrefs = {
    categories: { ...prefs.categories },
    types: { ...prefs.types },
  }
  MANDATORY_NOTIFICATION_CATEGORIES.forEach((c) => {
    next.categories[c] = true
  })
  Object.keys(NOTIFICATION_TYPE_DEFS).forEach((t) => {
    if (NOTIFICATION_TYPE_DEFS[t].alwaysOn) next.types[t] = true
  })
  rolePrefs[roleId] = next
}

interface IDemoSeed {
  id: string
  type: string
  title: string
  detail: string
  siteName: string | null
  minutesAgo: number
  link?: string | null
}

const SEEDS: IDemoSeed[] = [
  { id: 'own-1', type: 'request.own_outcome', title: 'Your annual leave request was approved', detail: '3–7 Aug · approved by HR Manager', siteName: null, minutesAgo: 18 },
  { id: 'own-2', type: 'shift.own_confirmation', title: 'Confirm your shift for tomorrow', detail: 'Morning shift 06:00–14:00 · confirm by 20:00 today', siteName: 'North Pit', minutesAgo: 40 },
  { id: 'req-1', type: 'request.submitted', title: 'Ethan Walker requested annual leave', detail: '5 days, starting Monday — Haulage', siteName: null, minutesAgo: 34 },
  { id: 'req-2', type: 'request.sla_breach', title: 'Request pending 4 days', detail: 'Shift change for Amara Mensah has passed the 48h response target', siteName: null, minutesAgo: 128 },
  { id: 'req-3', type: 'request.overtime_cap', title: 'Overtime request exceeds weekly cap', detail: 'Approving would put Kofi Adjei at 62h this week (cap 56h)', siteName: null, minutesAgo: 208 },
  { id: 'saf-1', type: 'safety.fatigue_limit', title: 'Fatigue limit reached', detail: 'Luis Herrera is rostered for a 7th consecutive shift tomorrow', siteName: 'North Pit', minutesAgo: 62 },
  { id: 'saf-2', type: 'safety.fitness_gap', title: 'Fitness-for-work clearance missing', detail: 'Diego Soto is rostered tonight but has no medical clearance after sick leave', siteName: 'North Pit', minutesAgo: 96 },
  { id: 'saf-3', type: 'safety.absence_pattern', title: 'Absence pattern flagged', detail: '3 consecutive Monday absences — Samuel Osei, Maintenance', siteName: null, minutesAgo: 1420 },
  { id: 'cmp-1', type: 'compliance.contract_expiring', title: 'Contract expires in 14 days', detail: 'Francis Boakye — Haulage, renewal not started', siteName: null, minutesAgo: 310 },
  { id: 'cmp-2', type: 'compliance.document_expiring', title: 'Work permit expires in 21 days', detail: 'Elena Vasquez — permit renewal takes ~30 days', siteName: null, minutesAgo: 350 },
  { id: 'cmp-3', type: 'compliance.cert_critical_role', title: 'MECH-2 certification expiring', detail: 'Only certified holder on site expires in 12 days — required by Maintenance coverage model', siteName: 'North Pit', minutesAgo: 400 },
  { id: 'ppl-1', type: 'people.returns_today', title: '3 workers return from leave today', detail: 'Ana Silva, Joseph Kimani, Pedro Alves — confirm arrival at check-in', siteName: null, minutesAgo: 180 },
  { id: 'ppl-2', type: 'people.no_show_after_leave', title: 'No-show after leave', detail: 'Diego Soto did not check in — was due back for Morning shift', siteName: 'North Pit', minutesAgo: 74 },
  { id: 'ppl-3', type: 'people.arrival_readiness', title: 'Arrival tomorrow has no bed assigned', detail: 'New hire Tomas Ricci arrives 07:00 — Camp B has no allocation yet', siteName: null, minutesAgo: 240 },
  { id: 'ppl-4', type: 'people.celebration', title: 'Birthday today', detail: 'Amara Mensah — Operations', siteName: null, minutesAgo: 460 },
  { id: 'acc-1', type: 'accommodation.guest_overstay', title: 'Guest overstay', detail: 'Room B-14 occupant is 2 days past approved duration', siteName: null, minutesAgo: 230 },
  { id: 'acc-2', type: 'accommodation.checkout_overdue', title: '2 check-outs overdue', detail: 'Due yesterday — beds still held in Block C', siteName: null, minutesAgo: 260 },
  { id: 'acc-3', type: 'accommodation.occupancy_threshold', title: 'Camp occupancy at 91%', detail: '12-person intake arrives Thursday — projected shortfall of 5 beds', siteName: null, minutesAgo: 300 },
  { id: 'acc-4', type: 'accommodation.meal_mismatch', title: 'Meal plan below occupancy', detail: 'Dinner planned for 190, camp occupancy 220', siteName: null, minutesAgo: 330 },
  { id: 'pay-1', type: 'payroll.cutoff_pending', title: '6 requests pending at payroll cutoff', detail: 'Cutoff is tomorrow 17:00 — unresolved requests will miss this cycle', siteName: null, minutesAgo: 45 },
  { id: 'pay-2', type: 'payroll.timesheet_anomaly', title: 'Missing clock-out', detail: 'Night shift — 2 workers have open 16h+ entries', siteName: 'South Ridge', minutesAgo: 150 },
  { id: 'dat-1', type: 'data.hygiene', title: '4 employees missing contact number', detail: 'Shift-change notifications cannot reach them', siteName: null, minutesAgo: 520 },
  { id: 'dat-2', type: 'data.delivery_failure', title: 'Shift reminder SMS bounced', detail: 'Number on file for Joseph Kimani is unreachable', siteName: null, minutesAgo: 88 },
  { id: 'ops-1', type: 'ops.machine_down', title: 'Dozer D1 breakdown — recovery in progress', detail: 'Task 9106 · recovery crew dispatched', siteName: 'North Pit', minutesAgo: 75, link: '/operations/today' },
  { id: 'ops-2', type: 'ops.maintenance_due', title: 'Crusher liner inspection is overdue', detail: 'Planned window missed by 1 day', siteName: 'North Pit', minutesAgo: 30, link: '/operations/today' },
  { id: 'ops-3', type: 'ops.task_backlog', title: 'Haul cycle #344 has no available machine', detail: 'Task blocked · reassign or split load', siteName: 'North Pit', minutesAgo: 5, link: '/operations/today' },
  { id: 'ops-4', type: 'ops.shift_review_ready', title: 'Night shift handover ready for review', detail: 'Summary and open issues attached', siteName: 'North Pit', minutesAgo: 390, link: '/operations/today' },
  { id: 'fue-1', type: 'fuel.low_stock', title: 'Diesel stock below 20% threshold', detail: 'Main tank North Pit · ~2.5 days at current burn', siteName: 'North Pit', minutesAgo: 110, link: '/fuel/dashboard' },
  { id: 'fue-2', type: 'fuel.consumption_spike', title: 'Excavator EX-2 consumption spike', detail: '18% above fleet baseline this week', siteName: 'North Pit', minutesAgo: 470, link: '/fuel/machines' },
  { id: 'mac-1', type: 'machinery.service_due', title: 'Haul truck HT-07 service due', detail: '3 engine hours remaining to service window', siteName: 'North Pit', minutesAgo: 200 },
  { id: 'mac-2', type: 'machinery.health_alert', title: 'Loader L-3 hydraulic temperature trending high', detail: 'Second occurrence this week — inspection advised', siteName: 'North Pit', minutesAgo: 65 },
  { id: 'inv-1', type: 'inventory.low_stock', title: 'Ground engaging tools below minimum stock', detail: '4 items under reorder point', siteName: null, minutesAgo: 320 },
  { id: 'inv-2', type: 'inventory.stocktake_due', title: 'Warehouse A stock-take due this week', detail: 'Last full count 92 days ago', siteName: null, minutesAgo: 900 },
  { id: 'pro-1', type: 'procurement.po_approval', title: 'PO-2214 (tyres) awaiting approval', detail: '2 days in queue · supplier holds quote until Friday', siteName: null, minutesAgo: 250 },
  { id: 'pro-2', type: 'procurement.po_overdue', title: 'PO-2190 delivery 6 days overdue', detail: 'Filter elements · supplier contacted twice', siteName: null, minutesAgo: 520 },
]

const stateFor = (id: string): { read: boolean; acknowledged: boolean } => {
  if (!readState[id]) readState[id] = { read: false, acknowledged: false }
  return readState[id]
}

const toItem = (seed: IDemoSeed, now: number): INotificationItem => {
  const def = notificationTypeDef(seed.type)
  const st = stateFor(seed.id)
  return {
    id: seed.id,
    type: seed.type,
    category: def.category,
    severity: def.severity,
    title: seed.title,
    detail: seed.detail,
    siteName: seed.siteName,
    createdAt: new Date(now - seed.minutesAgo * 60000).toISOString(),
    read: st.read,
    requiresAck: def.requiresAck,
    acknowledged: st.acknowledged,
    link: seed.link !== undefined ? seed.link : def.link,
  }
}

const coverageSeeds = (): IDemoSeed[] => {
  const out: IDemoSeed[] = []
  coverageSites().forEach((site) => {
    const summary = buildDemoWidgetSummary(site.id)
    summary.shifts.forEach((shift: any) => {
      if (shift.phase === 'past') return
      shift.departments.forEach((dept: any) => {
        if (dept.criticalGap) {
          out.push({
            id: `cov-crit-${site.id}-${dept.departmentId}-${shift.shiftId}`,
            type: 'coverage.critical_unfilled',
            title: `Critical post unfilled — ${dept.name}`,
            detail: `${shift.name} (${shift.windowFrom}–${shift.windowTo}) · ${dept.covered}/${dept.required} covered${dept.gapLabel ? ` · ${dept.gapLabel}` : ''}`,
            siteName: summary.siteName,
            minutesAgo: 12,
          })
        } else if (dept.covered < dept.required) {
          out.push({
            id: `cov-under-${site.id}-${dept.departmentId}-${shift.shiftId}`,
            type: 'coverage.under_required',
            title: `${dept.name} under required staffing`,
            detail: `${shift.name} (${shift.windowFrom}–${shift.windowTo}) · ${dept.covered}/${dept.required} covered`,
            siteName: summary.siteName,
            minutesAgo: 26,
          })
        }
      })
      if (shift.phase === 'future' && shift.confirmed != null && shift.confirmed < shift.required) {
        out.push({
          id: `cov-conf-${site.id}-${shift.shiftId}`,
          type: 'coverage.unconfirmed_roster',
          title: `Roster not fully confirmed — ${shift.name}`,
          detail: `${shift.confirmed} of ${shift.required} confirmed · starts ${shift.windowFrom}`,
          siteName: summary.siteName,
          minutesAgo: 52,
        })
      }
    })
  })
  return out
}

export const buildDemoNotifications = (): INotificationItem[] => {
  const now = Date.now()
  const items = [...coverageSeeds(), ...SEEDS].map((s) => toItem(s, now))
  return items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
}

export const demoNotificationPayload = (
  rootState: any,
  roleId?: number | null,
): { items: INotificationItem[]; unreadCount: number } => {
  syncCoverageRegistries(rootState)
  if (roleId !== undefined) activeRoleId = roleId
  let items = buildDemoNotifications()
  if (activeRoleId != null) {
    const prefs = getRoleNotificationPrefs(activeRoleId)
    items = items.filter((i) => {
      if (notificationTypeDef(i.type).alwaysOn) return true
      return prefs.categories[i.category] !== false && prefs.types[i.type] !== false
    })
  }
  return { items, unreadCount: items.filter((i) => !i.read).length }
}

export const markDemoNotificationRead = (id: string): void => {
  stateFor(id).read = true
}

export const markAllDemoNotificationsRead = (): void => {
  buildDemoNotifications().forEach((i) => {
    stateFor(i.id).read = true
  })
}

export const acknowledgeDemoNotification = (id: string): void => {
  stateFor(id).acknowledged = true
  stateFor(id).read = true
}
